import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

import { RemoteService } from './remote.service';
import { AuthenticationService } from './authentication.service';
import { StorageService } from './storage.service';
import { Hotel } from '../models/view-models/hotel';
import { StoragePersistKeyEnum } from '../models/enums/storage-persist-key.enum';

/**
 * Service for managing the current user's hotel context.
 * 
 * This service maintains a cached hotel for the authenticated user,
 * providing both synchronous access and reactive subscriptions.
 * 
 * The storage is the source of truth, with the observable derived from it.
 * Changes via setCurrentHotel() update both atomically.
 */
@Injectable({
  providedIn: 'root',
})
export class CurrentHotelService {
  private readonly _currentHotel: BehaviorSubject<Hotel | null>;

  constructor(
    private remote: RemoteService,
    private auth: AuthenticationService,
    private storageService: StorageService
  ) {
    // Initialize from storage (source of truth)
    this._currentHotel = new BehaviorSubject<Hotel | null>(this.loadFromStorage());
  }

  /**
   * Observable for reactive subscriptions to hotel changes.
   * New subscribers receive the current value immediately (BehaviorSubject).
   */
  get currentHotel$(): Observable<Hotel | null> {
    return this._currentHotel.asObservable();
  }

  /**
   * Direct access to the current hotel value.
   * Equivalent to currentHotel$.value but more explicit.
   */
  get currentHotel(): Hotel | null {
    return this._currentHotel.value;
  }

  /**
   * Convenience getter for the most common use case.
   * Returns null if no hotel is set.
   */
  get hotelId(): number | null {
    return this._currentHotel.value?.Id ?? null;
  }

  /**
   * Legacy BehaviorSubject access for backward compatibility.
   * @deprecated Use currentHotel$ for subscriptions or currentHotel for sync access.
   */
  get currentHotelSubject(): BehaviorSubject<Hotel | null> {
    return this._currentHotel;
  }

  /**
   * Sets the current hotel, updating both storage and observable atomically.
   * @param hotel The hotel to set, or null to clear.
   */
  setCurrentHotel(hotel: Hotel | null): void {
    this.storageService.setData(StoragePersistKeyEnum.CurrentUserHotel, hotel);
    this._currentHotel.next(hotel);
  }

  /**
   * Gets the current hotel from storage.
   * Note: This reads directly from storage, not the observable.
   */
  getCurrentHotel(): Hotel | null {
    return this.storageService.getData<Hotel>(StoragePersistKeyEnum.CurrentUserHotel);
  }

  /**
   * Loads the current user's hotel from the API and caches it.
   * Only makes the API call if a user is logged in.
   * 
   * @returns The loaded hotel, or null if no user is logged in.
   */
  async loadCurrentHotel(): Promise<Hotel | null> {
    const user = this.auth.getUser();
    if (user == null) {
      return null;
    }

    const hotel = await this.remote.getRequest('Hotels/GetHotelLinkedToUser');
    this.setCurrentHotel(hotel);
    return hotel;
  }

  /**
   * Clears the current hotel from both storage and observable.
   * Typically called on logout.
   */
  clear(): void {
    this.setCurrentHotel(null);
  }

  /**
   * @deprecated Use loadCurrentHotel() instead. This method exists for backward compatibility.
   */
  async initCurrentHotel(): Promise<void> {
    await this.loadCurrentHotel();
  }

  private loadFromStorage(): Hotel | null {
    return this.storageService.getData<Hotel>(StoragePersistKeyEnum.CurrentUserHotel);
  }
}
